import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, BaseRouteReuseStrategy, Params } from '@angular/router';

/**
 * Route `data` key. `false` tells {@link EntryRouteReuseStrategy} never to keep this route's component
 * instance for DIFFERENT route params — not its own, and not those of a parent route.
 */
export const REUSE_ACROSS_PARAMS_KEY = 'reuseAcrossParams';

/**
 * Angular's default reuse rule, plus one opt-out per route.
 *
 * Angular keeps a component instance whenever the route CONFIG matches, whatever the params. For
 * the entry detail page that is wrong: `/bedrijfsmiddel/:id` and `/bedrijven/:id` are the same
 * `:name` → `:id` config, so a link from one detail page to another changed the URL while the page
 * kept showing the old entry. The page loads its entry once, in its constructor, and holds its
 * per-entry state (tab, mode, save markers, form) in a component-scoped DetailsService — a fresh
 * instance is the only way that state cannot leak from one entry into the next.
 *
 * Params are compared over the whole path from the root, so a module slug change (`:name`) counts
 * as much as an entry id change. Child routes that do not change params — the read/edit mode
 * children of `:id` — keep the instance, which is what makes a mode switch cheap.
 */
@Injectable()
export class EntryRouteReuseStrategy extends BaseRouteReuseStrategy {
  override shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    if (future.routeConfig !== curr.routeConfig) {
      return false;
    }
    if (future.routeConfig?.data?.[REUSE_ACROSS_PARAMS_KEY] !== false) {
      return true;
    }
    return sameParams(pathParams(future), pathParams(curr));
  }
}

function pathParams(snapshot: ActivatedRouteSnapshot): Params[] {
  return snapshot.pathFromRoot.map(route => route.params);
}

function sameParams(a: Params[], b: Params[]): boolean {
  return (
    a.length === b.length &&
    a.every((params, level) => {
      const other = b[level];
      const keys = Object.keys(params);
      return keys.length === Object.keys(other).length && keys.every(key => params[key] === other[key]);
    })
  );
}
